import React, {Component} from "react"

import {SpinnerSecondaryButton} from "./SpinnerSecondaryButton";
import {APIClient} from "../utils/APIClient";
import {API_URL} from "../settings/local_settings";
import {FaIcon} from "./FaIcon";
import { withTranslation } from 'react-i18next';

function isHttpUrl(value) {
    if (typeof value !== "string") return false;
    return /^https?:\/\//i.test(value.trim());
}

/**
 * Statuses del backend con copy propio. Cualquier otro (incluido uno nuevo) cae al genérico:
 * misma regla de la casa que en `channels/sheet/sheetIssues.js` — una clave que viene del backend
 * nunca indexa un catálogo sin fallback.
 */
const ERROR_MESSAGE_KEY_BY_STATUS = {
    'URL_IS_INVALID': 'csv_file_input.url_invalid',
    'URL_IS_SPREADSHEET_NOT_CSV': 'csv_file_input.url_is_spreadsheet_not_csv',
    'CAN_NOT_ACCESS_URL': 'csv_file_input.cannot_access_url',
    // El archivo SE LEE y su primera fila no trae nombres de columna. Antes caía en
    // `CAN_NOT_ACCESS_URL`, que manda a revisar el link o los permisos cuando lo que hay que
    // mirar es la planilla: pasa cada vez que alguien inserta una fila arriba del encabezado.
    'CSV_HAS_NO_HEADERS': 'csv_file_input.csv_has_no_headers',
    'NETWORK_ERROR': 'csv_file_input.network_error'
};

export class CSVFileInputClass extends Component {
    constructor(props) {
        super(props);

        this.state = {
            url: this.props.initialUrl || "",
            importing: false,
            imported: false,
            // Error del último intento de importar, INLINE. Antes esto era un `alert()` bloqueante
            // que además tapaba el campo que había que corregir.
            error: null
        }

        this.handleUrlOnChange = this.handleUrlOnChange.bind(this);
        this.handleImport = this.handleImport.bind(this);
    }

    handleUrlOnChange(event) {
        this.setState({
            url: event.target.value,
            imported: false,
            // El usuario está corrigiendo la URL: el error del intento anterior ya no la describe.
            error: null
        });
    };

    handleImport() {
        if (!this.state.url) return;
        this.setState({importing: true, error: null});
        this.getHeadersFromCSVFileUrl();
    }

    /**
     * El error se renderiza ACÁ ADENTRO, no se delega a un callback del consumidor: así cualquier
     * pantalla que monte este input hereda el mensaje sin tener que hacer nada, y no hay forma de
     * que un consumidor nuevo se coma el error por no cablear un handler.
     */
    showError(status) {
        const {t} = this.props;
        const key = ERROR_MESSAGE_KEY_BY_STATUS[status] || 'csv_file_input.generic_error';
        this.setState({error: t(key)});
    }

    renderImportButton() {
        const {t} = this.props;
        if (this.state.importing) {
            return <SpinnerSecondaryButton style={{minWidth: "115px"}} className="btn"/>;
        }
        return (
            <button type="button"
                    className="btn"
                    onClick={this.handleImport}
                    disabled={!this.state.url}>
                {t('csv_file_input.import')}
            </button>
        );
    }

    /**
     * El copy de `url_is_spreadsheet_not_csv` son instrucciones numeradas separadas por `\n` (venían
     * de un `alert()`). Cada línea va como párrafo propio; las vacías eran espaciado del alert.
     */
    renderError() {
        if (!this.state.error) return null;

        const lines = String(this.state.error).split('\n').filter((line) => line.trim() !== '');

        return (
            <div className="csv-file-input-error" role="alert" aria-live="assertive">
                <FaIcon iconName="triangle-exclamation" className="csv-file-input-error-icon"/>
                <div className="csv-file-input-error-body">
                    {lines.map((line, index) => <p key={index}>{line}</p>)}
                </div>
            </div>
        );
    }

    render() {
        const {t, placeholder} = this.props;
        const canOpenUrl = isHttpUrl(this.state.url);
        return (
            // Wrapper sin clase A PROPÓSITO: existe sólo para que el componente tenga UNA raíz (la
            // fila del input y el bloque de error son hermanos) y no hay nada que declararle — el
            // error trae su propio `margin-top` en `.csv-file-input-error`. Una clase sin regla es
            // un gancho falso: promete un estilo que no existe.
            <div>
                <div className="sheet-import-hero-row">
                    <input type="text"
                           name="url"
                           value={this.state.url}
                           onChange={this.handleUrlOnChange}
                           placeholder={placeholder || ""}
                           autoComplete="off"
                           spellCheck={false}/>
                    {/* Sin `title=` nativo: la convención del FE es `warning-tooltip` y el
                        `aria-label` ya nombra la acción (para el lector de pantalla y para los
                        tests). Dos mecanismos de tooltip conviviendo es lo que se quiere evitar. */}
                    {canOpenUrl && (
                        <a href={this.state.url}
                           target="_blank"
                           rel="noopener noreferrer"
                           className="csv-file-input-open"
                           aria-label={t('csv_file_input.open_in_new_tab')}>
                            <FaIcon iconName="external-link-alt"/>
                        </a>
                    )}
                    {this.renderImportButton()}
                </div>
                {this.renderError()}
            </div>
        );
    }

    /**
     * `onImport` recibe UN objeto, no seis posicionales.
     *
     * Eran seis y el backend acaba de sumar un séptimo dato (`date_format_inference`, la ambigüedad
     * del formato de fecha). Con posicionales, sumarlo es agregar el argumento en el lugar exacto y
     * en los dos lados; el día que se sume el octavo, igual. Un objeto nombrado hace que agregar un
     * campo sea aditivo y que un consumidor viejo no lea el dato equivocado por correrse un lugar.
     * Se puede cambiar la firma sin ceremonia porque este componente tiene UN solo consumidor
     * (`channels/sheet/AddAdAccountForm`); si aparece un segundo, el objeto ya lo protege.
     *
     * `date_format_inference` es progressive enhancement: un backend viejo no manda esa clave y
     * viaja `null` — el consumidor tiene que comportarse exactamente como antes.
     */
    getHeadersFromCSVFileUrl() {
        new APIClient(API_URL + `check-csv-file-url/`).call('PUT', {"csv_file_url": this.state.url})
            .then((response) => {
                this.setState({importing: false});

                if (response && response.status === 'OK') {
                    const {
                        headers, column_mapping, currency, timezone, date_format, date_format_inference,
                        sample_rows
                    } = response.data;
                    this.setState({imported: true, error: null});
                    this.props.onImport({
                        url: this.state.url,
                        headers: headers,
                        // Filas del archivo QUE SE ACABA DE IMPORTAR. Sin esto, el modal de edición
                        // cruzaba los headers nuevos contra la muestra de la URL vieja y el preview
                        // mostraba un valor de otro archivo, alineado por posición. Un backend sin
                        // deployar no manda la clave: `null` = sin preview, como antes.
                        sampleRows: sample_rows || null,
                        columnMapping: column_mapping,
                        currency: currency,
                        timezone: timezone,
                        dateFormat: date_format,
                        // Backend viejo: la clave no existe. `null`, no `undefined`, para que el
                        // consumidor no tenga que distinguir "no vino" de "no hay ambigüedad".
                        dateFormatInference: date_format_inference !== undefined ? date_format_inference : null
                    });
                    return;
                }

                // No se muestra `error_message` crudo del backend: puede filtrar info de otra
                // agencia (regla de aislamiento cross-agency).
                this.showError(response ? response.status : null);
            })
            // `APIClient` nunca rejecta: lo único que puede caer acá es una excepción nuestra
            // dentro del `.then()`. Sin esto sería una promise rejection sin manejar — el mismo
            // "no pasa nada" que rompió el paso 2 de este modal.
            .catch(() => {
                this.setState({importing: false});
                this.showError(null);
            });
    }
}

export const CSVFileInput = withTranslation()(CSVFileInputClass);
export default CSVFileInput;
